import styled from 'styled-components'
import { Checkbox, FormControlLabel } from '@mui/material'
import { CheckIcon } from 'assets/icons/check'
import LoadingSpinner from 'components/ui/LoadingSpinner'
import {
  SavePopover,
  SavePopoverContent,
  SavePopoverDescription,
  SavePopoverOption,
  SavePopoverTitle,
} from 'components/gps-editor/CourseGpsEditorElements.styles'
import SubmitButton from 'components/form/SubmitButton'

const checkboxIconStyles = {
  alignItems: 'center',
  backgroundColor: '#444444',
  border: '1px solid #757575',
  borderRadius: '4px',
  boxSizing: 'border-box' as const,
  display: 'inline-flex',
  height: 20,
  justifyContent: 'center',
  width: 20,
}

const checkboxCheckedIconStyles = {
  ...checkboxIconStyles,
  backgroundColor: '#0BB163',
  border: '1px solid #0BB163',
  color: '#ffffff',
  fontSize: '14px',
  fontWeight: 700,
  lineHeight: 1,
}

const checkboxSx = {
  color: '#ffffff',
  padding: 0,
  '&.Mui-disabled': {
    opacity: 0.5,
  },
}

const formControlLabelSx = {
  alignItems: 'flex-start',
  gap: '12px',
  margin: 0,
  color: '#ffffff',
  fontSize: '14px',
  fontWeight: 400,
  '& .MuiFormControlLabel-label': {
    color: '#ffffff',
    lineHeight: '20px',
  },
  '& .MuiFormControlLabel-label.Mui-disabled': {
    color: '#ffffff',
    opacity: 0.5,
  },
}

interface Props {
  anchorEl: HTMLElement | null
  canPopulateIncompleteCourse: boolean
  disabled: boolean
  makeOfficialDisabled: boolean
  makeOfficialDisabledReason: string | null
  makeOfficial: boolean
  open: boolean
  populateRemainingHoles: boolean
  isDraftSave: boolean
  status: RequestStatus
  onClose: () => void
  onMakeOfficialChange: (checked: boolean) => void
  onPopulateRemainingHolesChange: (checked: boolean) => void
  onSaveAndFinish: () => void
  onAcknowledgeSuccess: () => void
}

export default function CourseGpsEditorSavePopover({
  anchorEl,
  canPopulateIncompleteCourse,
  disabled,
  makeOfficialDisabled,
  makeOfficialDisabledReason,
  makeOfficial,
  open,
  populateRemainingHoles,
  isDraftSave,
  status,
  onClose,
  onMakeOfficialChange,
  onPopulateRemainingHolesChange,
  onSaveAndFinish,
  onAcknowledgeSuccess,
}: Props): JSX.Element {
  const isLoading = status === 'loading'
  const isSucceeded = status === 'succeeded'
  const buttonContent = isLoading ? (
    <ButtonContent>
      <LoadingSpinner size={22} />
      Saving...
    </ButtonContent>
  ) : isSucceeded ? (
    <ButtonContent>
      <CheckIcon width={22} height={22} />
    </ButtonContent>
  ) : isDraftSave ? (
    'Save draft'
  ) : (
    'Save & Finish'
  )
  const handleButtonClick = isSucceeded ? onAcknowledgeSuccess : onSaveAndFinish
  return (
    <SavePopover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <SavePopoverContent>
        <SavePopoverTitle>Save and finish editing</SavePopoverTitle>
        <SavePopoverOption>
          <FormControlLabel
            control={
              <Checkbox
                checked={makeOfficial}
                disabled={makeOfficialDisabled}
                icon={<span style={checkboxIconStyles} />}
                checkedIcon={
                  <span style={checkboxCheckedIconStyles}>
                    <CheckIcon />
                  </span>
                }
                onChange={(event) => onMakeOfficialChange(event.target.checked)}
                sx={checkboxSx}
              />
            }
            label="Make official"
            sx={formControlLabelSx}
          />
          {makeOfficialDisabled && makeOfficialDisabledReason && (
            <SavePopoverDescription $disabled>{makeOfficialDisabledReason}</SavePopoverDescription>
          )}
        </SavePopoverOption>
        {canPopulateIncompleteCourse && (
          <SavePopoverOption>
            <FormControlLabel
              control={
                <Checkbox
                  checked={populateRemainingHoles}
                  disabled={!canPopulateIncompleteCourse}
                  icon={<span style={checkboxIconStyles} />}
                  checkedIcon={
                    <span style={checkboxCheckedIconStyles}>
                      <CheckIcon />
                    </span>
                  }
                  onChange={(event) => onPopulateRemainingHolesChange(event.target.checked)}
                  sx={checkboxSx}
                />
              }
              label="Populate remaining holes"
              sx={formControlLabelSx}
            />
            <SavePopoverDescription $disabled={!canPopulateIncompleteCourse}>
              Reuse existing mapped layouts to complete the course as 18 playable holes
            </SavePopoverDescription>
          </SavePopoverOption>
        )}
        <SavePopoverButton
          status={'idle'}
          handleSubmit={handleButtonClick}
          text={buttonContent}
          disabled={!isSucceeded && (disabled || isLoading)}
        />
      </SavePopoverContent>
    </SavePopover>
  )
}

const SavePopoverButton = styled(SubmitButton)`
  width: 100%;
  margin-top: 8px;
  height: 30px;
  font-size: 14px;
  font-weight: 500;
`

const ButtonContent = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`
